/**
 * Clawback Reason Types and Constants
 *
 * Defines the categorization system for commission clawbacks.
 * Reasons are designed to help identify patterns for business improvement:
 * - Some clawbacks are expected (e.g., bridging loans) and should be priced accordingly
 * - Others indicate retention issues that can be addressed
 */

/**
 * Reason why a loan was clawed back (discharged within clawback period)
 */
export type ClawbackReason =
  | 'bridging_loan'           // By design - short-term loan, clawback expected
  | 'lost_to_competitor'      // Client refinanced with another broker quickly
  | 'property_sold'           // Property sold within clawback period
  | 'paid_off'                // Loan paid off early (windfall, inheritance, etc.)
  | 'better_rate'             // Client found better rate elsewhere
  | 'financial_hardship'      // Couldn't afford payments, forced sale
  | 'relationship_settlement' // Divorce/separation forced property sale
  | 'investment_sold'         // Investment property disposed of
  | 'lender_dissatisfaction'  // Poor lender service led to early exit
  | 'product_mismatch'        // Wrong product for client needs
  | 'customer_moved'          // Customer moved banks directly
  | 'deceased'                // Client passed away
  | 'unknown';                // Needs categorization

/**
 * Display labels for clawback reasons
 */
export const CLAWBACK_REASON_LABELS: Record<ClawbackReason, string> = {
  bridging_loan: 'Bridging Loan',
  lost_to_competitor: 'Lost to Competitor',
  property_sold: 'Property Sold',
  paid_off: 'Loan Paid Off',
  better_rate: 'Better Rate Elsewhere',
  financial_hardship: 'Financial Hardship',
  relationship_settlement: 'Relationship Settlement',
  investment_sold: 'Investment Sold',
  lender_dissatisfaction: 'Lender Dissatisfaction',
  product_mismatch: 'Product Mismatch',
  customer_moved: 'Customer Moved Banks',
  deceased: 'Deceased / Estate',
  unknown: 'Unknown',
};

/**
 * Short labels for compact UI (stat cards)
 */
export const CLAWBACK_REASON_SHORT_LABELS: Record<ClawbackReason, string> = {
  bridging_loan: 'Bridging',
  lost_to_competitor: 'Competitor',
  property_sold: 'Sold',
  paid_off: 'Paid Off',
  better_rate: 'Better Rate',
  financial_hardship: 'Hardship',
  relationship_settlement: 'Settlement',
  investment_sold: 'Investment',
  lender_dissatisfaction: 'Lender',
  product_mismatch: 'Mismatch',
  customer_moved: 'Moved',
  deceased: 'Deceased',
  unknown: 'Unknown',
};

/**
 * UI color themes for clawback reasons
 * Colors indicate actionability:
 * - Blue/Purple: Expected/acceptable (bridging, paid off)
 * - Red/Orange: Actionable issues (competitor, better rate)
 * - Gray: Neutral/life events (deceased, sold)
 */
export const CLAWBACK_REASON_COLORS: Record<ClawbackReason, { bg: string; text: string; border: string }> = {
  bridging_loan: { bg: 'bg-blue-900/30', text: 'text-blue-400', border: 'border-blue-500/30' },
  lost_to_competitor: { bg: 'bg-red-900/30', text: 'text-red-400', border: 'border-red-500/30' },
  property_sold: { bg: 'bg-purple-900/30', text: 'text-purple-400', border: 'border-purple-500/30' },
  paid_off: { bg: 'bg-green-900/30', text: 'text-green-400', border: 'border-green-500/30' },
  better_rate: { bg: 'bg-orange-900/30', text: 'text-orange-400', border: 'border-orange-500/30' },
  financial_hardship: { bg: 'bg-amber-900/30', text: 'text-amber-400', border: 'border-amber-500/30' },
  relationship_settlement: { bg: 'bg-gray-700', text: 'text-gray-400', border: 'border-gray-600' },
  investment_sold: { bg: 'bg-purple-900/30', text: 'text-purple-400', border: 'border-purple-500/30' },
  lender_dissatisfaction: { bg: 'bg-red-900/30', text: 'text-red-400', border: 'border-red-500/30' },
  product_mismatch: { bg: 'bg-yellow-900/30', text: 'text-yellow-400', border: 'border-yellow-500/30' },
  customer_moved: { bg: 'bg-yellow-900/30', text: 'text-yellow-400', border: 'border-yellow-500/30' },
  deceased: { bg: 'bg-gray-700', text: 'text-gray-400', border: 'border-gray-600' },
  unknown: { bg: 'bg-gray-700', text: 'text-gray-400', border: 'border-gray-600' },
};

/**
 * Display order for reasons (most actionable/common first)
 */
export const CLAWBACK_REASON_DISPLAY_ORDER: ClawbackReason[] = [
  'lost_to_competitor',     // Most actionable - retention issue
  'better_rate',            // Actionable - pricing issue
  'bridging_loan',          // Expected - should be tracked
  'property_sold',          // Common - life event
  'paid_off',               // Common - positive outcome
  'financial_hardship',     // Concerning pattern
  'lender_dissatisfaction', // Actionable - lender selection
  'product_mismatch',       // Actionable - needs analysis
  'investment_sold',        // Common for investors
  'relationship_settlement',// Life event
  'customer_moved',         // Retention opportunity
  'deceased',               // Life event
  'unknown',                // Needs categorization
];

/**
 * All clawback reasons as array
 */
export const ALL_CLAWBACK_REASONS: ClawbackReason[] = [...CLAWBACK_REASON_DISPLAY_ORDER];

/**
 * Aggregated metrics for clawbacks by reason
 */
export interface ClawbackMetricsByReason {
  total: number;
  totalAmountCents: number;
  byReason: Record<ClawbackReason, number>;
  byReasonAmountCents: Record<ClawbackReason, number>;
  needsCategorization: number;
  needsCategorizationAmountCents: number;
}

/**
 * Options for filtering clawbacks by categorization status
 */
export type ClawbackCategorizationStatus = 'all' | 'needs_categorization' | 'completed';
